import View from '@ckeditor/ckeditor5-ui/src/view';
import FocusCycler from '@ckeditor/ckeditor5-ui/src/focuscycler';
import submitHandler from '@ckeditor/ckeditor5-ui/src/bindings/submithandler';
import ViewCollection from '@ckeditor/ckeditor5-ui/src/viewcollection';
import injectCssTransitionDisabler from '@ckeditor/ckeditor5-ui/src/bindings/injectcsstransitiondisabler';
import FocusTracker from '@ckeditor/ckeditor5-utils/src/focustracker';
import KeystrokeHandler from '@ckeditor/ckeditor5-utils/src/keystrokehandler';
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview';
import GroupedItemsView from '@ckeditor/ckeditor5-link/src/ui/groupeditemsview';
import ContentStyleRadioList from './content-style-radiolist';

export default class ContentStylesFormView extends View {
	/**
	 * Create the content styles form view.
	 *
	 * @param {Locale} locale
	 */
	constructor( locale, contentStyleCommand ) {
		super( locale );

		const t = locale.t;

		/**
		 * Tracks information about DOM focus in the form.
		 *
		 * @readonly
		 * @member {module:utils/focustracker~FocusTracker}
		 */
		this.focusTracker = new FocusTracker();

		/**
		 * An instance of the {@link module:utils/keystrokehandler~KeystrokeHandler}.
		 *
		 * @readonly
		 * @member {module:utils/keystrokehandler~KeystrokeHandler}
		 */
		this.keystrokes = new KeystrokeHandler();

		this.set( 'value', contentStyleCommand.value );

		this.set( 'styleElements', contentStyleCommand.styleElements );

		/**
		 * The style radio view
		 *
		 * @member {module:ui/labeledfield/labeledfieldview~LabeledFieldView}
		 */
		this.styleRadioView =
			this._createStyleRadioButtons( contentStyleCommand );

		/**
		 * The Reset button view.
		 *
		 * @member {module:ui/button/buttonview~ButtonView}
		 */
		this.resetButtonView = this._createButton(
			t( 'Reset' ),
			'ck-button-reset',
			'reset'
		);

		/**
		 * The Cancel button view.
		 *
		 * @member {module:ui/button/buttonview~ButtonView}
		 */
		this.cancelButtonView = this._createButton(
			t( 'Cancel' ),
			'ck-button-cancel ck-outline',
			'cancel'
		);

		/**
		 * The Close button view.
		 *
		 * @member {module:ui/button/buttonview~ButtonView}
		 */
		this.saveButtonView = this._createButton( t( 'Save' ), 'ck-button-save' );
		this.saveButtonView.type = 'submit';

		this.buttonGroupView = new GroupedItemsView( this.locale, [
			this.cancelButtonView,
			this.resetButtonView,
			this.saveButtonView
		], 'ck-between' );

		this.children = this._createFormChildren();

		/**
		 * A collection of views that can be focused in the form.
		 *
		 * @readonly
		 * @protected
		 * @member {module:ui/viewcollection~ViewCollection}
		 */
		this._focusables = new ViewCollection();

		/**
		 * Helps cycling over {@link #_focusables} in the form.
		 *
		 * @readonly
		 * @protected
		 * @member {module:ui/focuscycler~FocusCycler}
		 */
		this._focusCycler = new FocusCycler( {
			focusables: this._focusables,
			focusTracker: this.focusTracker,
			keystrokeHandler: this.keystrokes,
			actions: {
				// Navigate form fields backwards using the Shift + Tab keystroke.
				focusPrevious: 'shift + tab',

				// Navigate form fields forwards using the Tab key.
				focusNext: 'tab'
			}
		} );

		this.setTemplate( {
			tag: 'form',

			attributes: {
				class: [ 'ck', 'ck-content-styles-form' ],

				// https://github.com/ckeditor/ckeditor5-link/issues/90
				tabindex: '-1'
			},

			children: this.children
		} );

		injectCssTransitionDisabler( this );
	}

	/**
	 * @inheritDoc
	 */
	render() {
		super.render();

		submitHandler( {
			view: this
		} );

		const childViews = [ this.styleRadioView, this.buttonGroupView ];

		childViews.forEach( v => {
			// Register the view as focusable.
			this._focusables.add( v );

			// Register the view in the focus tracker.
			this.focusTracker.add( v.element );
		} );

		// Start listening for the keystrokes coming from #element.
		this.keystrokes.listenTo( this.element );
	}

	/**
	 * Focuses the fist {@link #_focusables} in the form.
	 */
	focus() {
		this._focusCycler.focusFirst();
	}

	/**
	 * Create the radio button options for the styles.
	 */
	_createStyleRadioButtons( contentStyleCommand ) {
		const list = new ContentStyleRadioList( this.locale, this.styleElements );

		contentStyleCommand.on( 'set:value', ( evt, propertyName, newValue, oldValue ) => {
			// Refresh the active options on the list.
			list.refresh();
			// Set the list value.
			if ( list.value != newValue ) {
				list.value = newValue;
			}
		} );
		list.on( 'change:value', ( evt, name, value ) => {
			// Update the command to have the new list value.
			if ( contentStyleCommand.value != value ) {
				contentStyleCommand.value = value;
			}
		} );
		return list;
	}

	/**
	 * Creates a button view.
	 *
	 * @private
	 * @param {String} label The button label.
	 * @param {String} className The additional button CSS class name.
	 * @param {String} [eventName] An event name that the `ButtonView#execute` event will be delegated to.
	 * @returns {module:ui/button/buttonview~ButtonView} The button view instance.
	 */
	_createButton( label, className, eventName ) {
		const button = new ButtonView( this.locale );

		button.set( {
			label
		} );

		button.extendTemplate( {
			attributes: {
				// CMS9 added the 'ck-no-icon' class.
				class: className + ' ck-no-icon'
			}
		} );

		if ( eventName ) {
			button.delegate( 'execute' ).to( this, eventName );
		}

		return button;
	}

	_createFormChildren() {
		const children = this.createCollection();
		children.add( this.styleRadioView );
		children.add( this.buttonGroupView );

		return children;
	}
}
